<script setup lang="ts">
import { computed, ref } from 'vue'

import defaultImg from '@/assets/images/default-image.png'
import { getAllImageSizeByFormat } from '@/mixins/utils'
import type { ProductImageData } from '@/types-new/product'

type ImageUri = { uri: string }
type DataProp = ProductImageData | ImageUri

const props = defineProps({
  alt: {
    type: String,
    default: '',
  },
  imgClass: {
    type: String,
    default: 'embed-responsive-item image-cover',
  },
  height: {
    type: String,
    default: 'auto',
  },
  width: {
    type: String,
    default: 'auto',
  },
  data: {
    type: Object as () => DataProp,
    default: (): ImageUri => ({ uri: defaultImg }),
  },
  sizes: {
    type: String,
    // TODO: možná změnit
    default: '(min-width: 600px) 25vw, (min-width: 500px) 50vw, 100vw',
  },
  loading: {
    type: String as () => 'eager' | 'lazy' | undefined,
    default: 'eager',
  },
})

const hasError = ref(false)
const isUriData = computed(() => 'uri' in props.data)

const src = computed(() => {
  if (isUriData.value) return (props.data as ImageUri).uri
  return getAllImageSizeByFormat(props.data as ProductImageData, 'jpeg').llg
})

const getSrcset = (format: string): string => {
  if (isUriData.value) return ''
  return Object.values(props.data as ProductImageData)
    .map(({ formats, width }) => `${formats[`image/${format}`]} ${width}w`)
    .join(', ')
}

const webpSrcset = computed(() => hasError.value ? defaultImg : getSrcset('webp'))
const jpegSrcset = computed(() => hasError.value ? defaultImg : getSrcset('jpeg'))
</script>

<template>
  <picture>
    <template v-if="!isUriData">
      <source
        :srcset="webpSrcset"
        type="image/webp"
        :sizes="sizes"
      >
      <source
        :srcset="jpegSrcset"
        type="image/jpeg"
        :sizes="sizes"
      >
    </template>

    <img
      :fetchpriority="loading === 'lazy' ? 'low' : 'high'"
      :src="hasError ? defaultImg : src"
      :class="imgClass"
      :width="width"
      :height="height"
      :alt="alt"
      :loading="loading"
      @error="hasError = true"
    >
  </picture>
</template>
